import React, { useState, useEffect, useContext } from "react";
import "./Card.css";
import expIcon from "../../Assets/Images/exp-badge-icon.png";
import Button from "../../Common/Button/Button";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import {
  applyEllipsis,
  toCapitalize,
  roundUpExperience,
} from "../Common-Function/CommonFunctions";
import ActiveCandidateIcon from "../../Assets/Images/active-candidate-icon.svg";
import JobIcon from "../../Assets/Images/candidate-card-job-icon.png";
import { DynamicContext } from "../../Context/DynamicContext";
import { getStageColor } from "../constants/stageColors";
import LocationIcon from "../../Assets/Images/location_icon.svg";
import { fetchCallStages } from "../DynamicCallStage/DynamicCallStageMapping";

const Card = ({
  profileImage,
  name,
  role,
  experience,
  email,
  phone,
  location,
  skills,
  tag,
  cardColor,
  cardbackgroundColor,
  skillColor,
  skillbackgroundColor,
  tagBackgroundColor,
  avatarBackGroundColor,
  cardWidth,
  viewEvent = () => "",
  assignJobEvent = () => "",
  showView = true,
  showAssignJob = false,
  ref,
  isSelectMode = false,
  isSelected = false,
  handleCardSelect = () => "",
  matchPercentage,
  fitness,
  viewDisabled = false,
  cardBorder,
  applications,
  callStageMap,
}) => {
  const { isDarkMode } = useContext(DynamicContext);
  const [callStagesMap, setCallStagesMap] = useState({});

  useEffect(() => {
    if (callStageMap !== undefined) return;
    let isMounted = true;

    const loadCallStages = async () => {
      try {
        const response = await fetchCallStages();
        if (isMounted && response && typeof response === "object") {
          setCallStagesMap(response);
        }
      } catch (error) {
        console.error("Failed to fetch call stages:", error);
      }
    };

    loadCallStages();

    return () => {
      isMounted = false;
    };
  }, [callStageMap]);

  const applicationsArray = Array.isArray(applications) ? applications : [];
  const applicationsCount = applicationsArray.length;
  const getApplicationJobTitle = (application) =>
    application?.job_title ||
    application?.job?.title ||
    application?.title ||
    application?.jobTitle ||
    "N/A";
  const getApplicationStage = (application) => {
    const stage = application?.stage || application?.status;
    if (!stage) return "N/A";
    return (callStageMap ?? callStagesMap)[stage] || stage;
  };

  const skillsArray =
    typeof skills === "string"
      ? skills
          .split(",")
          .map((skill) => skill.trim())
          .filter((skill) => skill && skill.trim() !== "")
      : Array.isArray(skills)
        ? skills.filter((skill) => skill && skill.trim() !== "")
        : [];

  const getFitMeta = (percentage) => {
    const val = parseFloat(percentage);
    if (isNaN(val)) {
      return { label: "N/A", color: "#ccc" };
    }
    if (val <= 33) return { label: "Unlikely fit", color: "#EF4444" };
    if (val <= 66) return { label: "Partial fit", color: "#F59E0B" };
    if (val <= 90) return { label: "Good fit", color: "#3B82F6" };
    return { label: "Top fit", color: "#10B981" };
  };

  // When the backend re-ranker supplies a fitness tier, map the token to the
  // same label/color used above so the badge is identical whether the source is
  // the LLM tier or the match_percentage threshold. Returns null when no valid
  // tier is present, so callers fall back to the threshold logic.
  const getFitMetaFromTier = (tier) => {
    switch ((tier || "").toLowerCase()) {
      case "top":
        return { label: "Top fit", color: "#10B981" };
      case "good":
        return { label: "Good fit", color: "#3B82F6" };
      case "partial":
        return { label: "Partial fit", color: "#F59E0B" };
      case "unlikely":
        return { label: "Unlikely fit", color: "#EF4444" };
      default:
        return null;
    }
  };
  const [ellipsisLength, setEllipsisLength] = useState(() => {
    if (window.innerWidth > 1800) return 50;
    if (window.innerWidth <= 768 && window.innerWidth >= 425) return 30;
    if (window.innerWidth < 950) return 20;
    return 20;
  });

  const [visibleSkillsCount, setVisibleSkillsCount] = useState(() => {
    if (window.innerWidth >= 1720 && window.innerWidth <= 2560) return 6;
    return 2;
  });

  const displayedSkills = skillsArray.slice(0, visibleSkillsCount);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1800) {
        setEllipsisLength(50);
      } else if (window.innerWidth <= 768 && window.innerWidth >= 425) {
        setEllipsisLength(30);
      } else if (window.innerWidth < 950) {
        setEllipsisLength(20);
      } else {
        setEllipsisLength(20);
      }

      if (window.innerWidth >= 1720 && window.innerWidth <= 2560) {
        setVisibleSkillsCount(6);
      } else {
        setVisibleSkillsCount(2);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tierMeta = getFitMetaFromTier(fitness);
  const fitMeta = tierMeta || getFitMeta(matchPercentage);
  const hasMatch =
    tierMeta != null ||
    (matchPercentage !== undefined &&
      matchPercentage !== null &&
      !isNaN(parseFloat(matchPercentage)));
  return (
    <div
      className="card"
      style={{
        color: cardColor,
        background: cardbackgroundColor,
        width: cardWidth,
        border: hasMatch ? `2px solid ${fitMeta.color}` : cardBorder,
        position: "relative",
      }}
      ref={ref}
    >
      <div className="card-header-container">
        <div className="candidate-card-header">
          <div className="card-header-user">
            <div
              className="header-and-tags"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="card-header-text-container">
                {isSelectMode ? (
                  <div
                    className="selection-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCardSelect();
                      }}
                    />
                  </div>
                ) : (
                  <div className="candidate-icon">
                    <img
                      src={ActiveCandidateIcon}
                      alt="Candidate Icon"
                      className="card-candidate-icon"
                    />
                  </div>
                )}
                <div className="card-header-text">
                  <h3>
                    {name && typeof name === "string" && name.trim()
                      ? applyEllipsis(toCapitalize(name), 30)
                      : "N/A"}
                  </h3>
                  {role && (
                    <div
                      className="card-details-span-candidate"
                      style={{ minWidth: 0, overflow: "hidden" }}
                    >
                      {/* <img
                        src={JobIcon}
                        alt="Job"
                        className="card-icon"
                        style={{ width: "20px" }}
                      /> */}
                      <CustomTooltip title={role} arrow>
                        <p className="card-subheader-candidate">
                          {applyEllipsis(role, ellipsisLength)}
                        </p>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
              </div>

              <div className="card-header-status">
                {applicationsCount > 0 && (
                  <CustomTooltip
                    title={
                      <div className="applications-tooltip-list">
                        {applicationsArray.map((app, index) => (
                          <div
                            className="applications-tooltip-item"
                            key={app.application_id || app.id || index}
                          >
                            <span className="applications-tooltip-title">
                              {getApplicationJobTitle(app)}
                            </span>
                            <span
                              className="applications-tooltip-stage"
                              style={{
                                backgroundColor:
                                  getStageColor?.(getApplicationStage(app)) ||
                                  "#6860EE",
                              }}
                            >
                              {getApplicationStage(app)}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                    placement="top"
                    pill={false}
                    arrow
                  >
                    <div className="applications-count-badge">
                      <img
                        src={JobIcon}
                        alt="Assigned Jobs"
                        style={{ width: "14px" }}
                      />
                      <span>{applicationsCount}</span>
                    </div>
                  </CustomTooltip>
                )}
                {hasMatch && (
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        backgroundColor: fitMeta.color,
                        color: "#fff",
                        padding: "6px 14px",
                        fontSize: "12px",
                        fontWeight: "500",
                        textTransform: "capitalize",
                        borderTopRightRadius: "8px",
                        borderBottomLeftRadius: "12px",
                        borderTopLeftRadius: "0",
                        borderBottomRightRadius: "0",
                        margin: "0",
                      }}
                    >
                      {fitMeta.label}
                    </div>
                  </div>
                )}
                {/* {tag && (
                  <div className="card-status">
                    <span
                      className="status-badge"
                      style={{ background: tagBackgroundColor }}
                    >
                      {tag}
                    </span>
                  </div>
                )} */}
              </div>
            </div>
          </div>
          <div
            className="contact-info"
            style={{ minWidth: 0, overflow: "hidden", width: "100%" }}
          >
            <div className="card-exp-details-span">
              <img
                src={expIcon}
                alt="Experience"
                className="card-icon"
                style={{ width: "20px" }}
              />
              <p className="card-subheader-candidate">
                {roundUpExperience(experience)}
              </p>
            </div>
            {location && (
              <div
                className="card-details-span-candidate"
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <img
                  src={LocationIcon}
                  alt="Location"
                  className="card-icon"
                  style={{ width: "20px" }}
                />
                <CustomTooltip title={location} arrow>
                  <p className="card-subheader-candidate">
                    {applyEllipsis(location, ellipsisLength)}
                  </p>
                </CustomTooltip>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card-contact-info">
        <div className="skills-and-view-candidate-card">
          <div className="card-skills">
            <p
              style={{
                fontSize: "14px",
                fontWeight: "500",
                paddingLeft: "4px",
              }}
            >
              Skills
            </p>
            {displayedSkills.length > 0 ? (
              <div className="skills-container">
                {displayedSkills.map((skill, index) => (
                  <div className="card-skills-div" key={index}>
                    <span className="skill-badge">
                      {applyEllipsis(skill, 5)}
                    </span>
                  </div>
                ))}
                {skillsArray.length > visibleSkillsCount && (
                  <CustomTooltip
                    className="skill-badge"
                    pill={false}
                    title={
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px",
                          maxWidth: "300px",
                          maxHeight: "100px",
                          overflowY: "auto",
                        }}
                      >
                        {skillsArray.map((skill, index) => (
                          <span
                            key={index}
                            className="skill-tooltip-badge"
                            style={{
                              color: skillColor,
                              background: skillbackgroundColor,
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    }
                    placement="top"
                    arrow
                  >
                    <div className="card-skills-div">
                      <span
                        className="skill-badge"
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        +{skillsArray.length - visibleSkillsCount}
                      </span>
                    </div>
                  </CustomTooltip>
                )}
              </div>
            ) : (
              <p
                className="no-skills"
                style={{ paddingLeft: "4px", paddingTop: "4px" }}
              >
                —
              </p>
            )}
          </div>
          <div className="view-container">
            {showAssignJob && (
              <Button
                label="Assign Job"
                event={assignJobEvent}
                className="view"
                backgroundColor="var(--body__background)"
                color="#6860EE"
                border="1px solid #6860EE"
                style={{ padding: "4px 12px" }}
              />
            )}
            {showView && (
              <Button
                label="View"
                event={viewEvent}
                disabled={viewDisabled}
                className="view"
                backgroundColor="none"
                color={isDarkMode ? "#aea9ff" : "#6860EE"}
                border={isDarkMode ? "1px solid #aea9ff" : "1px solid #6860EE"}
                style={{
                  padding: "4px 12px",
                  cursor: viewDisabled ? "not-allowed" : "pointer",
                  opacity: viewDisabled ? 0.5 : 1,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;